import React, { useRef, useState } from 'react';
import { Flame, Waves, X, Download, Share2, Loader2, Zap, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';

const MILESTONES = [7, 14, 30, 50, 100, 200, 365];

const MILESTONE_TITLES = {
  7: 'Week Warrior',
  14: 'Two-Week Titan',
  30: 'Monthly Machine',
  50: 'Fifty & Fired Up',
  100: 'Century Surfer',
  200: 'Legendary',
  365: 'Year of Stoke',
};

const MILESTONE_PHRASES = {
  7: "a full week of stoke — you're locked in",
  14: 'two straight weeks — the ocean knows your name',
  30: "a whole month — you're part of the tide now",
  50: 'fifty days strong — unstoppable energy',
  100: 'triple digits — absolute legend status',
  200: 'two hundred days — the ocean bows to you',
  365: "a full year of stoke — you ARE the ocean",
};

export function getActiveMilestone(streak) {
  for (let i = MILESTONES.length - 1; i >= 0; i--) {
    if (streak >= MILESTONES[i]) return MILESTONES[i];
  }
  return null;
}

export function shouldShowMilestone(streak) {
  const milestone = getActiveMilestone(streak);
  if (!milestone) return false;
  const key = `streak_milestone_seen_${milestone}`;
  return !localStorage.getItem(key);
}

export function dismissMilestone(streak) {
  const milestone = getActiveMilestone(streak);
  if (milestone) {
    localStorage.setItem(`streak_milestone_seen_${milestone}`, '1');
  }
}

export default function StreakCard({ user, onClose }) {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  const streak = user?.login_streak || 0;
  const milestone = getActiveMilestone(streak);
  const title = MILESTONE_TITLES[milestone] || 'Streak Master';
  const phrase = MILESTONE_PHRASES[milestone] || `${streak} days of pure stoke`;
  const referralCode = user?.referral_code || '';
  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const captureCard = async () => {
    if (!cardRef.current || generating) return null;
    setGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
      });
      return canvas;
    } catch {
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    const canvas = await captureCard();
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `surfswell-streak-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleShare = async () => {
    const canvas = await captureCard();
    if (!canvas) return;
    try {
      const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
      if (navigator.canShare && blob) {
        const file = new File([blob], 'surfswell-streak.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `${streak}-day streak on SurfSwell.ai`,
            text: `${streak} days of stoke — ${title} on SurfSwell.ai!`,
            files: [file],
          });
          return;
        }
      }
      if (blob) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        alert('Copied to clipboard!');
      }
    } catch (e) {
      if (e.name !== 'AbortError') handleDownload();
    }
  };

  const handleClose = () => {
    dismissMilestone(streak);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="streak-card-overlay"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="relative z-10 w-full max-w-sm"
      >
        <button
          data-testid="streak-card-close-btn"
          onClick={handleClose}
          className="absolute -top-3 -right-3 z-20 w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity shadow-lg"
          style={{ backgroundColor: '#000', border: '2px solid rgba(255,255,255,0.2)' }}
        >
          <X className="w-4 h-4" style={{ color: '#fff' }} />
        </button>

        {/* ====== THE CARD ====== */}
        <div
          ref={cardRef}
          className="rounded-3xl overflow-hidden shadow-2xl"
          style={{ background: 'linear-gradient(135deg, #1a0020 0%, #2d0040 20%, #5b1a8a 45%, #ff6b35 75%, #ff9a56 100%)' }}
          data-testid="streak-share-card"
        >
          <div className="relative" style={{ minHeight: 420 }}>
            {/* Glow overlay */}
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(ellipse at 50% 40%, rgba(255,107,53,0.15) 0%, transparent 60%), radial-gradient(ellipse at 30% 80%, rgba(91,26,138,0.1) 0%, transparent 50%)',
            }} />

            <div className="relative z-10 p-6 flex flex-col" style={{ minHeight: 420 }}>
              {/* Top — title badge */}
              <div className="flex items-center justify-between mb-auto">
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full"
                  style={{ background: 'rgba(255,107,53,0.15)', color: '#ff9a56' }}
                >
                  {title}
                </span>
                <span className="text-xs font-medium" style={{ color: 'rgba(255,154,86,0.5)' }}>
                  {dateStr}
                </span>
              </div>

              {/* Center — streak hero */}
              <div className="my-auto text-center py-4">
                {/* Fire icon cluster */}
                <div className="flex items-center justify-center gap-1 mb-3">
                  {[...Array(Math.min(streak >= 100 ? 5 : streak >= 30 ? 4 : streak >= 14 ? 3 : 2, 5))].map((_, i) => (
                    <Flame
                      key={i}
                      className="w-5 h-5"
                      style={{
                        color: '#ff6b35',
                        filter: 'drop-shadow(0 0 6px rgba(255,107,53,0.4))',
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </div>

                {/* Streak number */}
                <p className="font-bold leading-none" style={{ fontSize: 72, color: '#fff', textShadow: '0 2px 20px rgba(255,107,53,0.4)' }}>
                  {streak}
                </p>
                <p className="text-lg font-semibold mt-0.5" style={{ color: 'rgba(255,255,255,0.85)' }}>
                  day streak
                </p>
                <p className="text-sm mt-3 italic" style={{ color: 'rgba(255,154,86,0.7)' }}>
                  {phrase}
                </p>
              </div>

              {/* Bottom — info + branding */}
              <div className="mt-auto">
                {/* Mini stats */}
                <div
                  className="grid grid-cols-2 gap-2 rounded-2xl p-3 mb-4"
                  style={{ background: 'rgba(255,107,53,0.06)', border: '1px solid rgba(255,107,53,0.12)' }}
                >
                  <div className="text-center">
                    <Calendar className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: 'rgba(255,154,86,0.6)' }} />
                    <p className="text-sm font-bold" style={{ color: '#ff9a56' }}>{user?.best_login_streak || streak}</p>
                    <p className="text-[9px]" style={{ color: 'rgba(255,154,86,0.4)' }}>best streak</p>
                  </div>
                  <div className="text-center">
                    <Zap className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: 'rgba(255,154,86,0.6)' }} />
                    <p className="text-sm font-bold" style={{ color: '#ff9a56' }}>{user?.total_logins || 0}</p>
                    <p className="text-[9px]" style={{ color: 'rgba(255,154,86,0.4)' }}>total logins</p>
                  </div>
                </div>

                {/* Branding strip */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Waves className="w-4 h-4" style={{ color: '#ff9a56' }} />
                    <span
                      className="text-xs font-bold tracking-wide"
                      style={{ color: '#ff9a56' }}
                      data-testid="streak-card-brand"
                    >
                      SurfSwell.ai{referralCode ? `/r/${referralCode}` : ''}
                    </span>
                  </div>
                  <span className="text-[10px]" style={{ color: 'rgba(255,154,86,0.3)' }}>
                    Streak Milestone
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ====== ACTION BUTTONS ====== */}
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            data-testid="streak-card-share-btn"
            onClick={handleShare}
            disabled={generating}
            className="flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-2xl hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ background: '#ff6b35', color: '#fff' }}
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Share
          </button>
          <button
            data-testid="streak-card-download-btn"
            onClick={handleDownload}
            disabled={generating}
            className="flex items-center gap-2 text-sm font-medium px-6 py-3 rounded-2xl hover:opacity-80 transition-opacity shadow-md disabled:opacity-50"
            style={{ backgroundColor: '#000', color: '#fff' }}
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Save
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
